import { useEffect, useMemo, useState } from "react";
import { assetApi } from "shared/api/client/orval";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import type { Asset, Category } from "../model/asset.model";

export type CatalogEntry = {
  asset: Asset;
  category: string;
  uploadTime: string;
};

type RawRecord = Record<string, unknown>;

let cachedEntries: CatalogEntry[] | null = null;
let inFlightRequest: Promise<CatalogEntry[]> | null = null;

function asRecord(value: unknown): RawRecord {
  return typeof value === "object" && value !== null
    ? (value as RawRecord)
    : {};
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  const values = record["$values"];
  if (Array.isArray(values)) return values;
  const items = record.items;
  if (Array.isArray(items)) return items;
  const data = record.data;
  if (Array.isArray(data)) return data;
  return [];
}

function readString(obj: RawRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string") {
      return value;
    }
  }
  return "";
}

function readNumber(obj: RawRecord, ...keys: string[]): number {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}

function readArray(obj: RawRecord, ...keys: string[]): unknown[] {
  for (const key of keys) {
    const value = obj[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function formatPrice(price: number): string {
  if (!Number.isFinite(price) || price <= 0) return "Free";
  return `€${price.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";
  if (seconds < 60) return `${seconds} sec`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

function imageUrlFromFileName(fileName: string): string {
  if (!fileName) return "";
  return `${getApiOrigin()}/api/Image/stream/${encodeURIComponent(fileName)}`;
}

function toCatalogEntry(summary: RawRecord, details?: RawRecord): CatalogEntry {
  const detailsSafe = details ?? {};
  const assetId = readNumber(summary, "id", "ID");
  const title =
    readString(detailsSafe, "title", "Title") ||
    readString(summary, "title", "Title") ||
    "Untitled";
  const description =
    readString(detailsSafe, "description", "Description") ||
    readString(summary, "description", "Description");
  const category =
    readString(detailsSafe, "category", "Category") ||
    readString(summary, "category", "Category") ||
    "Uncategorized";
  const genre =
    readString(detailsSafe, "genre", "Genre") ||
    readString(summary, "genre", "Genre");
  const format =
    readString(detailsSafe, "format", "Format") ||
    readString(summary, "format", "Format");
  const language =
    readString(detailsSafe, "language", "Language") ||
    readString(summary, "language", "Language");
  const uploadTime =
    readString(summary, "uploadTime", "UploadTime") ||
    readString(detailsSafe, "uploadTime", "UploadTime");

  const priceRaw =
    readString(summary, "price", "Price") ||
    readString(detailsSafe, "price", "Price");
  const priceValue =
    priceRaw !== ""
      ? Number.parseFloat(priceRaw)
      : readNumber(summary, "price", "Price") ||
        readNumber(detailsSafe, "price", "Price");

  const images = readArray(detailsSafe, "images", "Images").map(asRecord);
  const thumbnailFileName =
    readString(
      images.find(
        (image) =>
          readString(image, "role", "Role").toLowerCase() === "thumbnail"
      ) ?? {},
      "fileName",
      "FileName"
    ) ||
    readString(summary, "thumbnail", "Thumbnail", "thumbnailFileName", "ThumbnailFileName") ||
    readString(
      images.find(
        (image) =>
          readString(image, "role", "Role").toLowerCase() === "heroimage"
      ) ?? {},
      "fileName",
      "FileName"
    );

  const videos = readArray(detailsSafe, "videos", "Videos").map(asRecord);
  const mainVideo =
    videos.find(
      (video) =>
        readString(video, "role", "Role").toLowerCase() === "mainvideo"
    ) ?? videos[0];
  const durationSeconds = mainVideo
    ? readNumber(mainVideo, "duration", "Duration")
    : 0;

  const tags = [genre, format].filter(Boolean).join(" • ") || language || "-";

  return {
    category,
    uploadTime,
    asset: {
      id: String(assetId),
      assetUploadId: assetId,
      title,
      description,
      thumbnailUrl: imageUrlFromFileName(thumbnailFileName),
      tags,
      price: formatPrice(priceValue),
      duration: formatDuration(durationSeconds),
    },
  };
}

async function loadCatalogEntries(): Promise<CatalogEntry[]> {
  if (cachedEntries) {
    return cachedEntries;
  }

  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = (async () => {
    const allAssetsResponse = await assetApi.getApiAssetGetAllAssets();
    const summaries = asArray(allAssetsResponse.data).map(asRecord);

    const publishedSummaries = summaries.filter((item) => {
      const status = readString(item, "status", "Status").toLowerCase();
      return status === "published";
    });

    const source = publishedSummaries.length > 0 ? publishedSummaries : summaries;

    const details = await Promise.all(
      source.map(async (summary) => {
        try {
          const id = readNumber(summary, "id", "ID");
          if (!id) {
            return { summary, details: null as RawRecord | null };
          }
          const detailResponse = await assetApi.getApiAssetAssetUploadIdDetails(id);
          return {
            summary,
            details: asRecord(detailResponse.data),
          };
        } catch {
          return {
            summary,
            details: null as RawRecord | null,
          };
        }
      })
    );

    const mapped = details
      .map(({ summary, details: detail }) => toCatalogEntry(summary, detail ?? undefined))
      .filter((entry) => Boolean(entry.asset.id));

    mapped.sort((a, b) => {
      const at = Date.parse(a.uploadTime || "");
      const bt = Date.parse(b.uploadTime || "");
      return (Number.isFinite(bt) ? bt : 0) - (Number.isFinite(at) ? at : 0);
    });

    cachedEntries = mapped.length > 0 ? mapped : null;
    inFlightRequest = null;
    return mapped;
  })();

  try {
    return await inFlightRequest;
  } finally {
    inFlightRequest = null;
  }
}

function buildCategories(entries: CatalogEntry[]): Category[] {
  const grouped = new Map<string, Asset[]>();

  for (const entry of entries) {
    const key = (entry.category || "Uncategorized").trim() || "Uncategorized";
    const existing = grouped.get(key) ?? [];
    grouped.set(key, [...existing, entry.asset]);
  }

  return Array.from(grouped.entries()).map(([name, assets]) => ({
    id:
      name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") ||
      "uncategorized",
    name,
    assets,
  }));
}

export function useRealCatalog() {
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let isMounted = true;

    void loadCatalogEntries()
      .then((result) => {
        if (!isMounted) return;
        setEntries(result);
        setError("");
      })
      .catch((e: unknown) => {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : "Failed to load catalog");
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const categories = useMemo(() => buildCategories(entries), [entries]);
  const assets = useMemo(() => entries.map((entry) => entry.asset), [entries]);

  return {
    entries,
    categories,
    assets,
    loading,
    error,
  };
}
